import { useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ChartDensity, ChartMode, ChartPaletteOption, ChartSettings } from 'components/charts/types/chart-settings.type';

// The old key is read once for migration; new writes use the shared chart key.
const STORAGE_KEY = 'bluviu.chart-settings';
const LEGACY_STORAGE_KEY = 'bluviu.data-lab.settings';

type PersistedChartSettings = Omit<ChartSettings, 'prefersReducedMotion'>;

const DEFAULT_SETTINGS: PersistedChartSettings = {
  mode: 'dark',
  palette: 'verified',
  animationsEnabled: true,
  gridlinesEnabled: true,
  legendsEnabled: true,
  density: 'comfortable',
};

type State = {
  settings: ChartSettings;
  baseTheme: Theme;
  setMode: (mode: ChartMode) => void;
  setPalette: (palette: ChartPaletteOption) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
  setGridlinesEnabled: (enabled: boolean) => void;
  setLegendsEnabled: (enabled: boolean) => void;
  setDensity: (density: ChartDensity) => void;
};

const ChartSettingsContext = React.createContext<State | undefined>(undefined);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeSettings = (value: unknown): PersistedChartSettings => {
  if (!isRecord(value)) return DEFAULT_SETTINGS;

  return {
    mode: value.mode === 'light' ? 'light' : 'dark',
    palette: value.palette === 'legacy' || value.palette === 'colorBlindSafe' ? value.palette : 'verified',
    animationsEnabled: typeof value.animationsEnabled === 'boolean' ? value.animationsEnabled : true,
    gridlinesEnabled: typeof value.gridlinesEnabled === 'boolean' ? value.gridlinesEnabled : true,
    legendsEnabled: typeof value.legendsEnabled === 'boolean' ? value.legendsEnabled : true,
    density: value.density === 'compact' ? 'compact' : 'comfortable',
  };
};

const readPersistedSettings = (): PersistedChartSettings => {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return normalizeSettings(JSON.parse(stored));

    const legacyStored = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    return legacyStored === null ? DEFAULT_SETTINGS : normalizeSettings(JSON.parse(legacyStored));
  } catch (error) {
    console.error('Chart settings could not be read from local storage.', error);
    return DEFAULT_SETTINGS;
  }
};

const ChartSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const baseTheme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [persisted, setPersisted] = useState<PersistedChartSettings>(readPersistedSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    } catch (error) {
      console.error('Chart settings could not be saved to local storage.', error);
    }
  }, [persisted]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      if (event.newValue === null) {
        setPersisted(DEFAULT_SETTINGS);
        return;
      }

      try {
        setPersisted(normalizeSettings(JSON.parse(event.newValue)));
      } catch (error) {
        console.error('Chart settings could not be read from a different tab.', error);
        setPersisted(DEFAULT_SETTINGS);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const settings = useMemo<ChartSettings>(
    () => ({ ...persisted, prefersReducedMotion }),
    [persisted, prefersReducedMotion]
  );

  const setMode = useCallback((mode: ChartMode) => setPersisted((current) => ({ ...current, mode })), []);
  const setPalette = useCallback(
    (palette: ChartPaletteOption) => setPersisted((current) => ({ ...current, palette })),
    []
  );
  const setAnimationsEnabled = useCallback(
    (enabled: boolean) => setPersisted((current) => ({ ...current, animationsEnabled: enabled })),
    []
  );
  const setGridlinesEnabled = useCallback(
    (enabled: boolean) => setPersisted((current) => ({ ...current, gridlinesEnabled: enabled })),
    []
  );
  const setLegendsEnabled = useCallback(
    (enabled: boolean) => setPersisted((current) => ({ ...current, legendsEnabled: enabled })),
    []
  );
  const setDensity = useCallback((density: ChartDensity) => setPersisted((current) => ({ ...current, density })), []);

  const value = useMemo(
    () => ({ settings, baseTheme, setMode, setPalette, setAnimationsEnabled, setGridlinesEnabled, setLegendsEnabled, setDensity }),
    [settings, baseTheme, setMode, setPalette, setAnimationsEnabled, setGridlinesEnabled, setLegendsEnabled, setDensity]
  );

  return <ChartSettingsContext.Provider value={value}>{children}</ChartSettingsContext.Provider>;
};

const useChartSettingsState = (): State => {
  const context = useContext(ChartSettingsContext);
  if (context === undefined) {
    throw new Error('useChartSettingsState must be used within a ChartSettingsProvider');
  }
  return context;
};

const useOptionalChartSettings = (): State | undefined => useContext(ChartSettingsContext);

export { ChartSettingsProvider, useChartSettingsState, useOptionalChartSettings };
